// Admin → Org Administration → Headquarters settings.
// Corporate billing identity used on manufacturing purchase orders.

import { useCallback, useEffect, useState } from "react";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import { requireSupabase } from "@/lib/supabase";

type HqDraft = {
  hq_phone: string;
  hq_email: string;
  hq_billing_address: string;
};

const BLANK: HqDraft = {
  hq_phone: "",
  hq_email: "",
  hq_billing_address: "",
};

export function OrgHeadquartersSection(props: { organizationId: string }) {
  const { organizationId } = props;
  const sb = requireSupabase();
  const [original, setOriginal] = useState<HqDraft>(BLANK);
  const [draft, setDraft] = useState<HqDraft>(BLANK);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await sb
        .from("organizations")
        .select("hq_phone, hq_email, hq_billing_address")
        .eq("id", organizationId)
        .maybeSingle();
      if (error) throw error;
      const row = (data ?? {}) as Record<string, string | null>;
      const loaded: HqDraft = {
        hq_phone: row.hq_phone?.trim() || "",
        hq_email: row.hq_email?.trim() || "",
        hq_billing_address: row.hq_billing_address?.trim() || "",
      };
      setOriginal(loaded);
      setDraft(loaded);
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e);
      if (!/does not exist|schema cache|Could not find/i.test(errMsg)) {
        setMsg({ text: errMsg, ok: false });
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, sb]);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty =
    draft.hq_phone !== original.hq_phone ||
    draft.hq_email !== original.hq_email ||
    draft.hq_billing_address !== original.hq_billing_address;

  async function save() {
    setMsg(null);
    setSaving(true);
    try {
      const { error } = await sb.rpc("admin_update_org_headquarters", {
        p_org_id: organizationId,
        p_hq_phone: draft.hq_phone.trim(),
        p_hq_email: draft.hq_email.trim(),
        p_hq_billing_address: draft.hq_billing_address.trim(),
      });
      if (error) throw error;
      setOriginal({ ...draft });
      setMsg({ text: "Headquarters settings saved.", ok: true });
      window.dispatchEvent(new CustomEvent("org-settings-saved"));
    } catch (e) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3 px-1 py-2">
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Corporate billing identity for purchase orders. On a generated PO, the purchasing
        agent&apos;s email and direct phone replace the headquarters contact lines.
      </p>

      {msg ? (
        <div
          className={
            msg.ok
              ? "rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100"
              : "rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-900/30 dark:text-rose-100"
          }
        >
          {msg.text}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="font-semibold text-gray-700 dark:text-zinc-200">Main phone</span>
          <input
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
            value={draft.hq_phone}
            disabled={loading || saving}
            onChange={(e) => setDraft((d) => ({ ...d, hq_phone: e.target.value }))}
            placeholder="(555) 555-0100"
          />
        </label>
        <label className="block text-xs">
          <span className="font-semibold text-gray-700 dark:text-zinc-200">
            General inquiries email
          </span>
          <input
            type="email"
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
            value={draft.hq_email}
            disabled={loading || saving}
            onChange={(e) => setDraft((d) => ({ ...d, hq_email: e.target.value }))}
            placeholder="orders@example.com"
          />
        </label>
      </div>

      <label className="block text-xs">
        <span className="font-semibold text-gray-700 dark:text-zinc-200">
          Corporate billing address
        </span>
        <div className="mt-1">
          <AddressAutocompleteInput
            value={draft.hq_billing_address}
            onChange={(v) => setDraft((d) => ({ ...d, hq_billing_address: v }))}
            disabled={loading || saving}
            placeholder="Street, city, state, ZIP"
          />
        </div>
      </label>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!isDirty || loading || saving}
          onClick={() => void save()}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save headquarters"}
        </button>
      </div>
    </div>
  );
}
